import { ChallengeType, ChallengeStatus, Partner } from './database';

// Enhanced challenge types for gamification system
export interface ChallengeTemplate {
  id: string;
  name: string;
  description: string;
  challenge_type: ChallengeType;
  target_amount?: number;
  target_days?: number;
  points_reward: number;
  difficulty: ChallengeDifficulty;
  category_id?: string;
  icon: string;
  duration_days: number;
  is_popular: boolean;
}

export interface ActiveChallenge {
  id: string;
  couple_id: string;
  name: string;
  description: string;
  challenge_type: ChallengeType;
  target_amount?: number;
  target_days?: number;
  points_reward: number;
  start_date: string;
  end_date: string;
  status: ChallengeStatus;
  current_progress: number;
  progress_percentage: number;
  days_remaining: number;
  is_on_track: boolean;
  difficulty: ChallengeDifficulty;
  icon: string;
  category_id?: string;
  category_name?: string;
  category_color?: string;
  category_icon?: string;
}

export interface ChallengeHistoryItem {
  id: string;
  couple_id: string;
  name: string;
  description: string;
  challenge_type: ChallengeType;
  points_reward: number;
  start_date: string;
  end_date: string;
  completed_date?: string;
  status: ChallengeStatus;
  final_progress: number;
  difficulty: ChallengeDifficulty;
  icon: string;
  points_earned: number;
}

export interface ChallengeProgress {
  challenge_id: string;
  current_progress: number;
  progress_percentage: number;
  is_completed: boolean;
  is_failed: boolean;
  days_remaining: number;
  is_on_track: boolean;
}

export interface StreakData {
  current_streak: number;
  best_streak: number;
  streak_type: StreakType;
  last_updated: string;
  next_milestone: StreakMilestone | null;
}

export interface StreakMilestone {
  days: number;
  points_reward: number;
  title: string;
  description: string;
}

// Request/Response types
export interface StartChallengeRequest {
  couple_id: string;
  template_id: string;
  start_date: string;
}

export interface StartChallengeResponse {
  success: boolean;
  challenge?: ActiveChallenge;
  error?: string;
}

export interface UpdateChallengeProgressRequest {
  couple_id: string;
  challenge_id: string;
  new_progress: number;
  expense_id?: string;
}

export interface UpdateChallengeProgressResponse {
  success: boolean;
  challenge?: ActiveChallenge;
  completed_challenges?: ChallengeHistoryItem[];
  points_awarded?: number;
  new_achievements?: string[];
  error?: string;
}

export interface GetActiveChallengesResponse {
  success: boolean;
  challenges?: ActiveChallenge[];
  error?: string;
}

export interface GetAvailableChallengesResponse {
  success: boolean;
  templates?: ChallengeTemplate[];
  error?: string;
}

export interface GetChallengeHistoryResponse {
  success: boolean;
  history?: ChallengeHistoryItem[];
  stats?: ChallengeStats;
  error?: string;
}

export interface ChallengeStats {
  total_completed: number;
  total_failed: number;
  total_points_earned: number;
  completion_rate: number;
  current_month_completed: number;
  current_month_points: number;
  favorite_challenge_type: ChallengeType | null;
  longest_streak: number;
}

// Enums
export enum ChallengeDifficulty {
  EASY = 'easy',
  MEDIUM = 'medium',
  HARD = 'hard'
}

export enum StreakType {
  UNDER_BUDGET = 'under_budget',
  NO_SPENDING = 'no_spending',
  CHALLENGE_COMPLETION = 'challenge_completion',
  SAVINGS_GOAL = 'savings_goal'
}

// Validation types
export interface ChallengeValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// Default challenge templates
export const DEFAULT_CHALLENGE_TEMPLATES: ChallengeTemplate[] = [
  {
    id: 'no-dining-out-week',
    name: 'No Dining Out Week',
    description: 'Avoid restaurant and takeout spending for 7 consecutive days',
    challenge_type: ChallengeType.NO_SPEND_DAYS,
    target_days: 7,
    points_reward: 150,
    difficulty: ChallengeDifficulty.MEDIUM,
    icon: 'utensils',
    duration_days: 7,
    is_popular: true,
  },
  {
    id: 'grocery-budget-100',
    name: 'Grocery Budget Master',
    description: 'Keep grocery spending under $100 this week',
    challenge_type: ChallengeType.CATEGORY_LIMIT,
    target_amount: 100,
    points_reward: 100,
    difficulty: ChallengeDifficulty.EASY,
    icon: 'shopping-cart',
    duration_days: 7,
    is_popular: true,
  },
  {
    id: 'coffee-shop-freeze',
    name: 'Coffee Shop Freeze',
    description: 'No coffee shop purchases for 2 weeks',
    challenge_type: ChallengeType.NO_SPEND_DAYS,
    target_days: 14,
    points_reward: 200,
    difficulty: ChallengeDifficulty.HARD,
    icon: 'coffee',
    duration_days: 14,
    is_popular: true,
  },
  {
    id: 'weekly-spending-limit-300',
    name: 'Weekly Spending Limit',
    description: 'Keep total spending under $300 this week',
    challenge_type: ChallengeType.SPENDING_LIMIT,
    target_amount: 300,
    points_reward: 125,
    difficulty: ChallengeDifficulty.MEDIUM,
    icon: 'dollar-sign',
    duration_days: 7,
    is_popular: false,
  },
  {
    id: 'transportation-budget-50',
    name: 'Transport Challenge',
    description: 'Keep transportation costs under $50 this week',
    challenge_type: ChallengeType.CATEGORY_LIMIT,
    target_amount: 50,
    points_reward: 75,
    difficulty: ChallengeDifficulty.EASY,
    icon: 'car',
    duration_days: 7,
    is_popular: false,
  },
  {
    id: 'entertainment-free-weekend',
    name: 'Entertainment-Free Weekend',
    description: 'No entertainment spending for the weekend',
    challenge_type: ChallengeType.NO_SPEND_DAYS,
    target_days: 2,
    points_reward: 50,
    difficulty: ChallengeDifficulty.EASY,
    icon: 'film',
    duration_days: 2,
    is_popular: false,
  },
];

// Challenge calculation helpers
export const CHALLENGE_CONSTANTS = {
  POINTS_MULTIPLIER: {
    [ChallengeDifficulty.EASY]: 1,
    [ChallengeDifficulty.MEDIUM]: 1.5,
    [ChallengeDifficulty.HARD]: 2,
  },
  STREAK_MILESTONES: [
    { days: 3, points_reward: 25, title: 'Getting Started', description: '3 day streak' },
    { days: 7, points_reward: 75, title: 'One Week Strong', description: '7 day streak' },
    { days: 14, points_reward: 150, title: 'Two Week Champion', description: '14 day streak' },
    { days: 30, points_reward: 400, title: 'Monthly Master', description: '30 day streak' },
    { days: 60, points_reward: 800, title: 'Incredible Dedication', description: '60 day streak' },
    { days: 100, points_reward: 1500, title: 'Legendary Commitment', description: '100 day streak' },
  ],
  COMPLETION_RATE_THRESHOLDS: {
    EXCELLENT: 0.8,
    GOOD: 0.6,
    FAIR: 0.4,
  },
} as const;